import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {WorkNotification} from "src/app/interfaces/notification.interface";
import {WorksModel} from "src/app/models/WorksModel";
import {StyledRangeDirective} from "src/app/modules/core/directives/styled-range.directive";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {isBook, isComic, isManga} from "src/app/utils/works-type-guards.util";
import {WORK_QUICK_RATINGS} from "../../constants/work-quick-rating.constants";
import type {
  RatingPromptContext,
  WorkRatingSubmit,
} from "../../interfaces/add-work-rating-prompt.interface";
import {WorksService} from "../../services/works.service";
import {
  TouchStableRangeInput,
  WORK_RATING_DEFAULT,
  WORK_RATING_MAX,
  WORK_RATING_MIN,
  getWorkRatingLabelKey,
  normalizeWorkRating,
} from "../../utils/work-rating.util";

type QuickRatingWork = WorksModel | WorkNotification;

@Component({
  selector: "app-work-quick-rating",
  templateUrl: "./work-quick-rating.component.html",
  styleUrls: ["./work-quick-rating.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, StyledRangeDirective],
})
export class WorkQuickRatingComponent implements OnDestroy {
  @Input({required: true}) public userId!: string;
  @Input({required: true}) public work!: QuickRatingWork;
  @Input() public language = "en";
  @Input() public titleKey = "WORK_COMPANION_RATING_TITLE";
  @Input() public doneTitleKey = "WORK_COMPANION_RATING_DONE_TITLE";
  @Input() public successMessageKey = "WORK_COMPANION_RATING_SUCCESS";
  @Input() public ratingPromptContext?: RatingPromptContext;
  @Input() public autoCloseOnSuccessMs?: number;
  @Input() public showRemoveLink = false;
  @Input() public saveRating?: WorkRatingSubmit;
  @Output() public rated = new EventEmitter<number>();
  @Output() public requestClose = new EventEmitter<void>();
  @Output() public requestRemove = new EventEmitter<void>();

  public ratingSuccess = false;
  public ratingValue: number | null = null;
  public currentRating = WORK_RATING_DEFAULT;
  public isSubmittingRating = false;
  public readonly minRating = WORK_RATING_MIN;
  public readonly maxRating = WORK_RATING_MAX;
  public readonly quickRatings = WORK_QUICK_RATINGS;
  private readonly isBook = isBook;
  private readonly isManga = isManga;
  private readonly isComic = isComic;
  private readonly ratingTouchInput = new TouchStableRangeInput();
  private ratingBeforeTouch: number | null | undefined;
  private autoCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly worksService: WorksService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  public setQuickRating(value: number): void {
    if (this.isSubmittingRating) return;
    this.applyRatingInput(value);
    this.submitRating();
  }

  public get hasRatingValue(): boolean {
    return this.ratingValue !== null;
  }

  public get ratingLabelKey(): string {
    return getWorkRatingLabelKey(this.currentRating);
  }

  public onRatingInput(event: Event): void {
    if (this.isSubmittingRating) return;
    if (this.ratingTouchInput.shouldIgnorePostTouchInput()) {
      this.currentRating = this.ratingValue ?? WORK_RATING_DEFAULT;
      this.cdr.markForCheck();
      return;
    }

    if (this.ratingTouchInput.isScrolling) {
      this.restoreRatingBeforeTouch();
      return;
    }

    const rating = normalizeWorkRating(
      (event.target as HTMLInputElement).value
    );
    this.applyRatingInput(rating);
    this.ratingTouchInput.record(rating);
  }

  public onTouchStart(event: TouchEvent): void {
    this.ratingBeforeTouch = this.ratingValue;
    this.ratingTouchInput.start(event);
  }

  public onTouchMove(event: TouchEvent): void {
    this.ratingTouchInput.move(event);
    if (this.ratingTouchInput.isScrolling) {
      this.restoreRatingBeforeTouch();
    }
  }

  public onTouchEnd(): void {
    const wasScrolling = this.ratingTouchInput.isScrolling;
    const rating = this.ratingTouchInput.end();

    if (wasScrolling) {
      this.restoreRatingBeforeTouch();
      this.ratingBeforeTouch = undefined;
      return;
    }

    this.ratingBeforeTouch = undefined;
    if (rating === null) {
      return;
    }

    this.applyRatingInput(rating);
  }

  public onTouchCancel(): void {
    this.ratingTouchInput.cancel();
    this.restoreRatingBeforeTouch();
    this.ratingBeforeTouch = undefined;
  }

  public get canSubmitRating(): boolean {
    const rating = this.ratingValue;

    return (
      rating !== null &&
      rating >= WORK_RATING_MIN &&
      rating <= WORK_RATING_MAX &&
      Boolean(this.work?._id) &&
      Boolean(this.work?.type) &&
      Boolean(this.userId)
    );
  }

  public getWorkTitle(work: QuickRatingWork): string {
    if (this.isBook(work) || this.isComic(work) || this.isManga(work)) {
      return work.title;
    }

    return this.worksService.getItemTitle(work);
  }

  public get contextKey(): string {
    return this.ratingPromptContext === "advanced"
      ? "WORK_COMPANION_RATING_CONTEXT_ADVANCED"
      : "WORK_COMPANION_RATING_CONTEXT_COMPLETED";
  }

  public submitRating(): void {
    const rating = this.ratingValue;

    if (!this.canSubmitRating || this.isSubmittingRating || rating === null) {
      return;
    }

    this.isSubmittingRating = true;
    this.cdr.markForCheck();

    const request = this.saveRating
      ? this.saveRating(rating)
      : this.worksService.updateWorkRatingByType(
          this.userId,
          this.work._id,
          this.work.type,
          rating
        );
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.ratingSuccess = true;
        this.isSubmittingRating = false;
        this.rated.emit(rating);
        this.scheduleAutoCloseIfNeeded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSubmittingRating = false;
        this.toastService.sendToast({
          text: "ERROR_UPDATE",
          classes: "bg-form-error",
        });
        this.cdr.markForCheck();
      },
    });
  }

  public skipRating(): void {
    if (!this.saveRating) {
      this.requestClose.emit();
      return;
    }
    if (this.isSubmittingRating) return;
    this.isSubmittingRating = true;
    this.saveRating()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.requestClose.emit(),
        error: () => {
          this.isSubmittingRating = false;
          this.toastService.sendToast({
            text: "ERROR_UPDATE",
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
  }

  private applyRatingInput(value: number): void {
    const rating = normalizeWorkRating(value);
    this.ratingValue = rating;
    this.currentRating = rating;
    this.cdr.markForCheck();
  }

  private restoreRatingBeforeTouch(): void {
    if (this.ratingBeforeTouch === undefined) {
      return;
    }

    this.ratingValue = this.ratingBeforeTouch;
    this.currentRating = this.ratingBeforeTouch ?? WORK_RATING_DEFAULT;
    this.cdr.markForCheck();
  }

  private scheduleAutoCloseIfNeeded(): void {
    if (!this.autoCloseOnSuccessMs || this.autoCloseOnSuccessMs <= 0) {
      return;
    }

    if (this.autoCloseTimeoutId) {
      clearTimeout(this.autoCloseTimeoutId);
    }

    this.autoCloseTimeoutId = setTimeout(() => {
      this.requestClose.emit();
    }, this.autoCloseOnSuccessMs);
  }

  ngOnDestroy(): void {
    if (this.autoCloseTimeoutId) {
      clearTimeout(this.autoCloseTimeoutId);
    }
  }
}
